import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Copy, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ShareSectionProps {
  referralCode: string;
  contestSlug: string;
  referralBonusEntries: number;
  baseUrl?: string;
}

export function ShareSection({ referralCode, contestSlug, referralBonusEntries, baseUrl }: ShareSectionProps) {
  const [copied, setCopied] = useState(false);

  // Use provided baseUrl or fallback to current origin
  const appBaseUrl = baseUrl || window.location.origin;
  const shareUrl = `${appBaseUrl}/c/${contestSlug}?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const entryLabel = referralBonusEntries === 1 ? "entry" : "entries";

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join this giveaway!",
          text: `Enter for a chance to win! I earn +${referralBonusEntries} ${entryLabel} when you use my link.`,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="fade-in rounded-xl border border-border bg-card p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Share2 className="w-4 h-4" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-lg leading-snug">
            Earn +{referralBonusEntries} {entryLabel} per friend
          </h3>
          <p className="text-muted-foreground text-sm mt-1">
            Share your link. When a friend enters with it, you get {referralBonusEntries} extra {entryLabel}.
          </p>
        </div>
      </div>

      <div className="bg-muted rounded-lg px-3 py-2.5 mb-3 overflow-hidden">
        <p className="text-sm font-mono truncate">{shareUrl}</p>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={handleCopy}
          className={cn(
            "flex-1 transition-all",
            copied && "border-success text-success"
          )}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </>
          )}
        </Button>

        {"share" in navigator && (
          <Button variant="entry" onClick={handleNativeShare} className="flex-1">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        )}
      </div>
    </div>
  );
}
